import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import config from '../config';

const lastUpdated = 'April 12, 2026';

const faqs = [
  {
    question: 'How do I cancel my ChessWiz subscription?',
    answer:
      'You can cancel your subscription from the same place where you started it. If you subscribed in the iPhone app, manage or cancel it from your Apple subscription settings. If you subscribed on the ChessWiz website, sign in there and cancel from your account billing settings.'
  },
  {
    question: 'Can I cancel in the app or on the website?',
    answer:
      'Yes. Cancellation can be done in the app or on the site, depending on where your subscription is managed. Apple-managed subscriptions are cancelled through Apple. Website-managed subscriptions are cancelled on the ChessWiz site after signing in.'
  },
  {
    question: 'Will I keep access after cancelling?',
    answer:
      'In general, cancellations stop future renewals and your paid access remains active until the end of the current billing period. If you are unsure about your renewal date, contact support and we can help you confirm it.'
  },
  {
    question: 'How do I contact support?',
    answer:
      'Email support@chesswiz.net and include the email address tied to your ChessWiz account plus a short description of the issue. We use that to find your account quickly and respond faster.'
  }
];

const SupportPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(46, 125, 50, 0.14), transparent 28%), linear-gradient(180deg, #F7F8F5 0%, #FFFFFF 38%, #F4F7FB 100%)'
      }}
    >
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#17351F' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            ChessWiz Support
          </Typography>
          <Link component={RouterLink} to="/privacy" color="inherit" sx={{ mr: 2 }}>
            Privacy Policy
          </Link>
          <Link component={RouterLink} to="/terms" color="inherit">
            Terms of Service
          </Link>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            border: '1px solid rgba(34, 44, 55, 0.08)',
            boxShadow: '0 24px 80px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            mb: 4
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              background:
                'linear-gradient(135deg, rgba(23, 53, 31, 0.98) 0%, rgba(39, 83, 48, 0.94) 48%, rgba(233, 240, 232, 0.94) 100%)',
              color: '#FFFFFF'
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: '0.12em', opacity: 0.8 }}>
              Support
            </Typography>
            <Typography variant="h3" component="h1" sx={{ mt: 1, mb: 1.5, color: '#FFFFFF' }}>
              Help with subscriptions and account support
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 700, color: 'rgba(255,255,255,0.82)' }}>
              This page is intended for App Store support and customer service requests. If you
              need help with billing, cancellations, or account access, start with the FAQ below or
              email support@chesswiz.net.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.72)' }}>
              Last updated: {lastUpdated}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2.5}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  bgcolor: '#F7FAF7',
                  border: '1px solid rgba(46, 125, 50, 0.18)'
                }}
              >
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Quick contact
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Reach out any time at{' '}
                    <Link href="mailto:support@chesswiz.net" underline="hover">
                      support@chesswiz.net
                    </Link>
                    .
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<EmailOutlinedIcon />}
                      href="mailto:support@chesswiz.net"
                      sx={{ borderRadius: 999 }}
                    >
                      Email support
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PublicOutlinedIcon />}
                      component="a"
                      href={config.clientUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ borderRadius: 999 }}
                    >
                      Open ChessWiz site
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Box>
                <Typography variant="h4" sx={{ mb: 1.5 }}>
                  Frequently asked questions
                </Typography>
                {faqs.map((faq) => (
                  <Accordion
                    key={faq.question}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      borderRadius: '18px !important',
                      border: '1px solid rgba(99, 115, 129, 0.2)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  elevation={0}
                  sx={{
                    flex: 1,
                    borderRadius: 4,
                    border: '1px solid rgba(99, 115, 129, 0.16)'
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <PhoneIphoneOutlinedIcon color="primary" />
                      <Typography variant="h6">Cancel in the app</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      If your subscription is billed by Apple, open your iPhone subscription
                      settings and cancel ChessWiz there.
                    </Typography>
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    flex: 1,
                    borderRadius: 4,
                    border: '1px solid rgba(99, 115, 129, 0.16)'
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <PublicOutlinedIcon color="primary" />
                      <Typography variant="h6">Cancel on the site</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      If your subscription is billed directly by ChessWiz, sign in on the site and
                      cancel it from your account billing settings.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SupportPage;
